import html2canvas from 'html2canvas';

interface CaptureOptions {
  width?: number;
  height?: number;
  scale?: number;
  paddingTop?: number;
  paddingBottom?: number;
}


interface ContainerStyle {
  backgroundFull?: string;
  background?: string;
  commonStyle?: Record<string, string>;
}

// 템플릿 컨텐츠 타입
interface TemplateContent {
  id: string | number;
  type: string;
  content?: any;
  data?: any;
  style?: any;
  [key: string]: any;
}

const DEFAULT_OPTIONS: CaptureOptions = {
  width: 630,
  height: 720,
  scale: 0.78,
  paddingTop: 30,
  paddingBottom: 30
};

/**
 * 현재 DOM의 content-list를 캡처하여 썸네일 생성
 * @param contents 템플릿 컨텐츠 데이터 (JSON 형태)
 * @param containerStyle 컨테이너 스타일 설정
 * @param options 캡처 옵션
 */
export const captureContents = async (
  contents: TemplateContent[],
  containerStyle: ContainerStyle,
  options: CaptureOptions = {}
): Promise<string | null> => {
  const config = { ...DEFAULT_OPTIONS, ...options };
  let wrapper: HTMLElement | null = null;

  try {

    // 1. 현재 렌더링된 content-list 찾기
    const contentList = document.querySelector('.content-list') as HTMLElement;
    if (!contentList) {
      return null;
    }

    // 2. 캡처용 임시 wrapper 생성
    wrapper = createCaptureWrapper(containerStyle, config);

    // 3. content-list 복제 및 스타일 적용
    const clonedContent = await prepareContentForCapture(contentList);

    wrapper.appendChild(clonedContent);
    document.body.appendChild(wrapper);

    // 5. 이미지 로드 완료 대기
    await waitForImagesToLoad(wrapper);

    // 6. html2canvas로 캡처
    const canvas = await html2canvas(wrapper, {
      width: config.width,
      height: config.height,
      windowWidth: config.width,
      backgroundColor: containerStyle?.backgroundFull || '#ffffff',
      allowTaint: false, // base64 변환했으므로 taint 방지
      scale: config.scale,
    });

    // 7. base64 반환
    const dataUrl = canvas.toDataURL('image/png');
    return dataUrl;

  } catch (error) {
    return null;
  } finally {
    // 정리: wrapper가 있으면 제거
    if (wrapper && wrapper.parentNode) {
      document.body.removeChild(wrapper);
    }
  }
};

/**
 * 캡처용 wrapper 엘리먼트 생성
 */
function createCaptureWrapper(containerStyle: ContainerStyle, config: CaptureOptions): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.style.cssText = `
    position: fixed;
    top: -10000px;
    left: -10000px;
    width: ${config.width}px;
    padding-top: ${config.paddingTop}px;
    padding-bottom: ${config.paddingBottom || 0}px;
    background-color: ${containerStyle?.backgroundFull || '#ffffff'};
    display: flex;
    justify-content: center;
    align-items: flex-start;
  `;
  return wrapper;
}

/**
 * 캡처를 위한 컨텐츠 준비
 */
async function prepareContentForCapture(contentList: HTMLElement): Promise<HTMLElement> {
  const clonedContent = contentList.cloneNode(true) as HTMLElement;
  clonedContent.style.transform = 'scale(0.8)';
  clonedContent.style.transformOrigin = 'top center';

  // 모든 외부 이미지를 base64로 변환하여 CORS 문제 해결
  await convertExternalImagesToBase64(clonedContent);

  return clonedContent;
}

/**
 * 외부 도메인 이미지들을 base64로 변환하여 CORS 문제 해결
 */
async function convertExternalImagesToBase64(element: HTMLElement): Promise<void> {
  const images = element.querySelectorAll('img') as NodeListOf<HTMLImageElement>;
  const currentOrigin = window.location.origin;

  const conversionPromises = Array.from(images).map(async (img) => {
    // 외부 도메인 이미지이고 이미 base64가 아닌 경우만 처리
    if (img.src && !img.src.startsWith(currentOrigin) && !img.src.startsWith('data:')) {
      try {
        const base64 = await imageToBase64(img.src);
        img.src = base64;
      } catch (error) {
        console.warn('Failed to convert image to base64:', img.src, error);
        // 변환 실패시 원본 이미지 유지
      }
    }
  });

  await Promise.all(conversionPromises);
}

/**
 * 이미지 URL을 base64로 변환
 */
async function imageToBase64(imageUrl: string): Promise<string> {
  try {
    const response = await fetch(`${imageUrl}?v=${new Date().getMilliseconds()}`, {
      cache: 'no-store',
    });

    const blob = await response.blob();
    const imageUrl_fetched = URL.createObjectURL(blob);

    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        try {
          canvas.width = img.width;
          canvas.height = img.height;
          ctx?.drawImage(img, 0, 0);
          const base64 = canvas.toDataURL('image/png');

          // 메모리 정리
          URL.revokeObjectURL(imageUrl_fetched);
          resolve(base64);
        } catch (error) {
          URL.revokeObjectURL(imageUrl_fetched);
          reject(error);
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(imageUrl_fetched);
        reject(new Error('Failed to load image'));
      };

      img.src = imageUrl_fetched;
    });
  } catch (error) {
    throw new Error(`Failed to fetch image: ${error}`);
  }
}


/**
 * 모든 이미지 로드 완료 대기
 */
async function waitForImagesToLoad(element: HTMLElement): Promise<void> {
  const images = element.querySelectorAll('img');
  const imagePromises = Array.from(images).map(img => {
    return new Promise<void>((resolve) => {
      if (img.complete) {
        resolve();
      } else {
        const handleLoad = () => {
          img.removeEventListener('load', handleLoad);
          img.removeEventListener('error', handleLoad);
          resolve();
        };
        img.addEventListener('load', handleLoad);
        img.addEventListener('error', handleLoad);

        // 최대 3초 대기 후 포기
        setTimeout(handleLoad, 3000);
      }
    });
  });

  await Promise.all(imagePromises);
}

